import { useMemo, useState, useEffect } from "react";

/**
 * Custom React hook for managing an editable copy of an array of entities.
 *
 * @param {Array} sourceArray - The original array to be edited.
 * @returns {[Array, Function, boolean, Function]} - Returns:
 *   [0] editableArray: a copy of the array that can be modified
 *   [1] setEditableArray: setter function to update the editable array
 *   [2] isChanged: boolean indicating if the editable array differs from the source
 *   [3] reset: function to reset the editable array to the source array
 *
 * The hook tracks changes between the source and editable array, including Date fields in objects.
 */
export const useEditableArray = (sourceArray) => {
    const safeSource = useMemo(() => sourceArray || [], [sourceArray]);

    // Keep an internal snapshot of the source so callers can reset to a provided source
    // and so change detection compares against the current source snapshot.
    const [sourceSnapshot, setSourceSnapshot] = useState(() => [...safeSource]);
    const [editableArray, setEditableArray] = useState(() => [...safeSource]);

    // If the incoming sourceArray reference changes, update the snapshot and reset editable state
    useEffect(() => {
        setSourceSnapshot([...safeSource]);
        setEditableArray([...safeSource]);
    }, [safeSource]);

    const isChanged = useMemo(() => {
        if (sourceSnapshot.length !== editableArray.length) return true;
        return sourceSnapshot.some((item, idx) => {
            const editableItem = editableArray[idx];
            if (!editableItem) return true;
            return Object.keys(item).some((key) => {
                const a = item[key];
                const b = editableItem[key];
                if (a instanceof Date && b instanceof Date) {
                    return a.getTime() !== b.getTime();
                }
                return a !== b;
            });
        });
    }, [sourceSnapshot, editableArray]);

    // Reset editable array. If newSource is provided, treat it as the new original source.
    const reset = (newSource) => {
        const src = newSource || sourceSnapshot || [];
        setSourceSnapshot([...src]);
        setEditableArray([...src]);
    };

    return [editableArray, setEditableArray, isChanged, reset];
};
